/**
 * DESERT FLORA (2026-11-05, owner: "yes" to swapping the canyon's green bushes,
 * pines and green mountains for desert plants and scenery).
 *
 * Built here from our own geometry (no third-party assets, no credits): a
 * saguaro, an organ-pipe cactus, a prickly pear, an agave rosette, a barrel
 * cactus, a soaptree yucca and a dead mesquite snag. Pure three.js maths (no
 * DOM, no textures), deterministic per seed, so `agent/bench-extras-canyon.mjs`
 * can measure them headless.
 *
 * Contract for every builder:
 *  - metres, base at y = 0, centred on the trunk / rosette at x = z = 0;
 *  - attributes `position`, `normal`, `color` (the look is vertex colour: the
 *    ribs, corky bases, dead-leaf skirts and weathered grain live in the
 *    geometry + colour, so one shared material draws them all) and `swayW`
 *    (the shared wind program, wind.ts): 0 on everything rigid — cacti and the
 *    snag never sway, which also keeps their cast shadow exact — and a little
 *    on yucca leaf tips and its flower stalk;
 *  - indexed, a few hundred triangles at most: each species is ONE
 *    InstancedMesh per map.
 */
import * as THREE from "three";

function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Geometry accumulator: several parts become one indexed geometry. */
class Acc {
  pos: number[] = [];
  col: number[] = [];
  sway: number[] = [];
  idx: number[] = [];
  get n(): number { return this.pos.length / 3; }
  v(x: number, y: number, z: number, c: THREE.Color, w = 0): number {
    this.pos.push(x, y, z);
    this.col.push(c.r, c.g, c.b);
    this.sway.push(w);
    return this.n - 1;
  }
  tri(a: number, b: number, c: number): void { this.idx.push(a, b, c); }
  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute("swayW", new THREE.Float32BufferAttribute(this.sway, 1));
    g.setIndex(this.idx);
    g.computeVertexNormals();
    g.computeBoundingBox();
    g.computeBoundingSphere();
    return g;
  }
}

const tc = new THREE.Color(), tc2 = new THREE.Color();
const T = new THREE.Vector3(), N = new THREE.Vector3(), B = new THREE.Vector3(), P = new THREE.Vector3();

/**
 * A RIBBED TUBE along a polyline (cactus stems, trunks, branches). Rings are
 * framed by parallel transport so a bending arm does not twist; `ribs` > 0
 * fluting pulls every other vertex in (a saguaro's accordion pleats), which the
 * smooth normals turn into lit ridges and shadowed grooves. The last point may
 * have radius 0: it becomes a single tip vertex (a domed crown). The bottom
 * stays open (it is buried).
 */
function tube(acc: Acc, path: THREE.Vector3[], radius: (i: number) => number, sides: number,
  ribDepth: number, colour: (i: number, groove: number, out: THREE.Color) => THREE.Color,
  sway: (i: number) => number = () => 0): void {
  const rings: number[] = [];
  N.set(1, 0, 0);
  for (let i = 0; i < path.length; i++) {
    const a = path[Math.max(0, i - 1)], b = path[Math.min(path.length - 1, i + 1)];
    T.subVectors(b, a).normalize();
    // parallel transport: project the previous normal off the new tangent
    N.addScaledVector(T, -N.dot(T));
    if (N.lengthSq() < 1e-6) N.set(0, 0, 1).addScaledVector(T, -T.z);
    N.normalize();
    B.crossVectors(T, N);
    const r = radius(i);
    rings.push(acc.n);
    if (r <= 1e-5) { // the tip
      acc.v(path[i].x, path[i].y, path[i].z, colour(i, 0, tc), sway(i));
      continue;
    }
    for (let k = 0; k < sides; k++) {
      const th = (k / sides) * Math.PI * 2;
      const groove = ribDepth > 0 ? (k % 2) : 0;
      const rr = r * (1 - ribDepth * groove);
      P.copy(path[i]).addScaledVector(N, Math.cos(th) * rr).addScaledVector(B, Math.sin(th) * rr);
      acc.v(P.x, P.y, P.z, colour(i, groove, tc), sway(i));
    }
  }
  for (let i = 0; i + 1 < path.length; i++) {
    const a = rings[i], b = rings[i + 1];
    const tip = radius(i + 1) <= 1e-5;
    for (let k = 0; k < sides; k++) {
      const k1 = (k + 1) % sides;
      if (tip) acc.tri(a + k, a + k1, b);
      else { acc.tri(a + k, a + k1, b + k1); acc.tri(a + k, b + k1, b + k); }
    }
  }
}

/** Dome the top of a stem: radius profile over the last `dome` metres. */
const domeR = (r: number, y: number, top: number, dome: number): number => {
  if (y < top - dome) return r;
  const f = Math.min(1, (y - (top - dome)) / dome);
  return r * Math.sqrt(Math.max(0, 1 - f * f));
};

/** Points of a straight segment, upward stem with a dome. */
function stemPath(x: number, z: number, y0: number, top: number, r: number, n: number, lean: [number, number]): { path: THREE.Vector3[]; rad: number[] } {
  const path: THREE.Vector3[] = [], rad: number[] = [];
  const dome = r * 1.05;
  const ys: number[] = [];
  for (let i = 0; i <= n; i++) ys.push(y0 + (top - dome - y0) * (i / n));
  // dome rings: 2 latitude rings + the tip
  ys.push(top - dome * 0.55, top - dome * 0.18, top);
  for (const y of ys) {
    const f = (y - y0) / Math.max(1e-3, top - y0);
    path.push(new THREE.Vector3(x + lean[0] * f * f, y, z + lean[1] * f * f));
    rad.push(y >= top - 1e-6 ? 0 : domeR(r, y, top, dome));
  }
  return { path, rad };
}

// ---------------------------------------------------------------------------
const SAG_SKIN = new THREE.Color("#5d6b47"), SAG_TIP = new THREE.Color("#7b8656");
const SAG_CORK = new THREE.Color("#7a6c55"), SAG_GROOVE = 0.62;

/** SAGUARO: a ribbed column, a corky grey base, 1-3 arms that leave the trunk
 *  horizontally, elbow and climb. ~4.2 m at scale 1. */
export function saguaroGeo(seed = 1): THREE.BufferGeometry {
  const rnd = mulberry(0x5a6 + seed * 101);
  const acc = new Acc();
  const H = 3.9 + rnd() * 0.5, R = 0.22;
  const paint = (y: number, groove: number, top: number, out: THREE.Color): THREE.Color => {
    out.copy(SAG_SKIN).lerp(SAG_TIP, Math.max(0, (y - (top - 0.45)) / 0.45) * 0.8);
    out.lerp(SAG_CORK, Math.max(0, 1 - y / 0.55) * 0.85);
    // faint vertical banding: drought years
    out.multiplyScalar(0.92 + 0.12 * Math.sin(y * 3.1 + seed));
    if (groove) out.multiplyScalar(SAG_GROOVE);
    return out;
  };
  const trunk = stemPath(0, 0, -0.1, H, R, 6, [0.06, -0.04]);
  tube(acc, trunk.path, (i) => trunk.rad[i], 16, 0.11, (i, g, o) => paint(trunk.path[i].y, g, H, o));
  const arms = 1 + Math.floor(rnd() * 2.6);
  const a0 = rnd() * Math.PI * 2;
  for (let k = 0; k < arms; k++) {
    const az = a0 + k * (Math.PI * 2 / arms) + (rnd() - 0.5) * 0.9;
    const dx = Math.cos(az), dz = Math.sin(az);
    const ya = 1.5 + rnd() * 1.1, out = 0.42 + rnd() * 0.12, rise = 0.8 + rnd() * 0.8, r = 0.13 + rnd() * 0.02;
    const top = Math.min(H - 0.3, ya + 0.25 + rise);
    const path: THREE.Vector3[] = [], rad: number[] = [];
    // out from inside the trunk, a rounded elbow, then up
    for (const [h, v] of [[0.05, 0], [out * 0.62, 0.02], [out, 0.13], [out + 0.05, 0.3]] as const) {
      path.push(new THREE.Vector3(dx * h, ya + v, dz * h)); rad.push(r);
    }
    const stem = stemPath(dx * (out + 0.06), dz * (out + 0.06), ya + 0.5, top, r, 2, [dx * 0.04, dz * 0.04]);
    for (let i = 0; i < stem.path.length; i++) { path.push(stem.path[i]); rad.push(stem.rad[i]); }
    tube(acc, path, (i) => rad[i], 10, 0.12, (i, g, o) => paint(Math.max(0.6, path[i].y), g, top, o));
  }
  return acc.build();
}

/** ORGAN PIPE: 6-9 slim ribbed stems from one root crown, splaying out then
 *  climbing. ~2.4-3.1 m at scale 1. */
export function organPipeGeo(seed = 1): THREE.BufferGeometry {
  const rnd = mulberry(0x0e9 + seed * 37);
  const acc = new Acc();
  const SKIN = new THREE.Color("#5b6a4a"), TIP = new THREE.Color("#7e8a5e"), BASE = new THREE.Color("#6f6450");
  const stems = 6 + Math.floor(rnd() * 3.5);
  for (let k = 0; k < stems; k++) {
    const az = (k / stems) * Math.PI * 2 + (rnd() - 0.5) * 0.6;
    const dx = Math.cos(az), dz = Math.sin(az);
    const top = 1.7 + rnd() * 1.4, r = 0.085 + rnd() * 0.03, spread = 0.18 + rnd() * 0.3;
    const path: THREE.Vector3[] = [], rad: number[] = [];
    const n = 4;
    for (let i = 0; i <= n; i++) {
      const y = -0.08 + (top - r - (-0.08)) * (i / n);
      // splay: quick outward bow near the root, then straight up
      const off = 0.04 + spread * (1 - Math.exp(-y * 3.2));
      path.push(new THREE.Vector3(dx * off, y, dz * off));
      rad.push(r * (i === 0 ? 0.8 : 1));
    }
    const tipOff = 0.04 + spread * (1 - Math.exp(-top * 3.2));
    path.push(new THREE.Vector3(dx * tipOff, top - r * 0.4, dz * tipOff)); rad.push(r * 0.75);
    path.push(new THREE.Vector3(dx * tipOff, top, dz * tipOff)); rad.push(0);
    tube(acc, path, (i) => rad[i], 8, 0.14, (i, g, o) => {
      const y = path[i].y;
      o.copy(SKIN).lerp(TIP, Math.max(0, (y - (top - 0.35)) / 0.35) * 0.8).lerp(BASE, Math.max(0, 1 - y / 0.35) * 0.8);
      if (g) o.multiplyScalar(0.64);
      return o;
    });
  }
  return acc.build();
}

/** A flat ellipsoid PAD (prickly pear), centred on its local origin, standing
 *  in its own x/y plane, then oriented by `m`. */
function pad(acc: Acc, m: THREE.Matrix4, w: number, h: number, t: number, base: THREE.Color, rim: THREE.Color): void {
  const LON = 6;
  const v = new THREE.Vector3();
  const put = (x: number, y: number, z: number, c: THREE.Color): number => {
    v.set(x, y, z).applyMatrix4(m);
    return acc.v(v.x, v.y, v.z, c);
  };
  // pad lies in x/y, thickness along z: poles are the faces' centres
  const front = put(0, 0, t, tc2.copy(base));
  const back = put(0, 0, -t, tc2.copy(base).multiplyScalar(0.85));
  const ring: number[] = [], inner0: number[] = [], inner1: number[] = [];
  for (let k = 0; k < LON; k++) {
    const a = (k / LON) * Math.PI * 2;
    const x = Math.cos(a) * w, y = Math.sin(a) * h;
    tc2.copy(base).lerp(rim, 0.55);
    ring.push(put(x, y, 0, tc2));
    inner0.push(put(x * 0.62, y * 0.62, t * 0.8, base));
    inner1.push(put(x * 0.62, y * 0.62, -t * 0.8, tc2.copy(base).multiplyScalar(0.85)));
  }
  for (let k = 0; k < LON; k++) {
    const k1 = (k + 1) % LON;
    acc.tri(front, inner0[k], inner0[k1]);
    acc.tri(inner0[k], ring[k], ring[k1]); acc.tri(inner0[k], ring[k1], inner0[k1]);
    acc.tri(back, inner1[k1], inner1[k]);
    acc.tri(inner1[k], ring[k1], ring[k]); acc.tri(inner1[k], inner1[k1], ring[k1]);
  }
}

/** PRICKLY PEAR: a branching stack of flat oval pads, the old ones at the
 *  bottom yellowed and corky, a few magenta fruits on the top rims.
 *  ~0.8 m at scale 1. */
export function pricklyPearGeo(seed = 1): THREE.BufferGeometry {
  const rnd = mulberry(0x9ea + seed * 53);
  const acc = new Acc();
  const GREEN = new THREE.Color("#6c8250"), BLUE = new THREE.Color("#748a64"), OLD = new THREE.Color("#8b8152");
  const RIM = new THREE.Color("#a29a62"), FRUIT = new THREE.Color("#8e2443");
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler();
  const one = new THREE.Vector3(1, 1, 1);
  type Pad = { c: THREE.Vector3; up: THREE.Vector3; h: number; gen: number };
  const pads: Pad[] = [];
  const fruitAt: THREE.Vector3[] = [];
  const grow = (c: THREE.Vector3, yaw: number, tilt: number, gen: number): void => {
    const w = 0.13 + rnd() * 0.035, h = 0.16 + rnd() * 0.04, t = 0.028;
    e.set(0, yaw, tilt, "YXZ");
    q.setFromEuler(e);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
    const centre = c.clone().addScaledVector(up, h * 0.92);
    m.compose(centre, q, one);
    const base = tc.copy(gen === 0 ? OLD : GREEN).lerp(BLUE, rnd() * 0.5).multiplyScalar(0.9 + rnd() * 0.18).clone();
    pad(acc, m, w, h, t, base, RIM);
    const p: Pad = { c: centre, up, h, gen };
    pads.push(p);
    if (gen >= 2) {
      if (rnd() < 0.7) fruitAt.push(centre.clone().addScaledVector(up, h * 0.95));
      return;
    }
    const kids = gen === 0 ? 1 + (rnd() < 0.6 ? 1 : 0) : (rnd() < 0.55 ? 1 : 0);
    const first = rnd() < 0.5 ? -1 : 1;
    for (let k = 0; k < kids; k++) {
      // a child sprouts from the parent's upper rim, off to one side, and
      // turns well away from its parent's plane: a real opuntia is a
      // splayed fan of pads facing every way, not a flat stack
      const sgn = k === 0 ? first : -first;
      const side = sgn * (0.35 + rnd() * 0.45);
      const rim = centre.clone().addScaledVector(up, h * 0.78);
      rim.x += Math.cos(yaw) * side * w * 1.1; rim.z -= Math.sin(yaw) * side * w * 1.1;
      grow(rim, yaw + sgn * (0.7 + rnd() * 0.8), tilt * 0.3 + side * 0.7, gen + 1);
    }
  };
  const roots = 2 + (rnd() < 0.5 ? 1 : 0);
  for (let k = 0; k < roots; k++) {
    const a = (k / roots) * Math.PI * 2 + rnd();
    grow(new THREE.Vector3(Math.cos(a) * 0.12, -0.04, Math.sin(a) * 0.12), a + Math.PI / 2 + (rnd() - 0.5) * 0.6, (rnd() < 0.5 ? -1 : 1) * (0.25 + rnd() * 0.35), 0);
  }
  // fruit: small squat octahedra (tunas)
  for (const f of fruitAt) {
    const r = 0.035;
    const ids = [[r, 0, 0], [-r, 0, 0], [0, r * 1.3, 0], [0, -r * 0.6, 0], [0, 0, r], [0, 0, -r]]
      .map(([x, y, z]) => acc.v(f.x + x, f.y + y, f.z + z, FRUIT));
    for (const [a, b, c] of [[0, 2, 4], [4, 2, 1], [1, 2, 5], [5, 2, 0], [4, 3, 0], [1, 3, 4], [5, 3, 1], [0, 3, 5]]) acc.tri(ids[a], ids[b], ids[c]);
  }
  return acc.build();
}

/** AGAVE: a rosette of thick, keeled, glaucous leaves; the outer ones lie
 *  out and curl down at the tip, the inner ones stand up round a tight
 *  central spike. ~0.65 m at scale 1. */
export function agaveGeo(seed = 1): THREE.BufferGeometry {
  const rnd = mulberry(0xa6a + seed * 71);
  const acc = new Acc();
  const LEAF = new THREE.Color("#7e978c"), DARK = new THREE.Color("#55695f"), EDGE = new THREE.Color("#a8a77a"), DRY = new THREE.Color("#8a7a58");
  const leaves = 15;
  const v = new THREE.Vector3(), side = new THREE.Vector3(), dir = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  for (let k = 0; k < leaves; k++) {
    const f = k / leaves;                       // 0 = outer whorl, 1 = heart
    const az = k * 2.39996 + rnd() * 0.3;       // golden angle phyllotaxis
    const el = 0.28 + f * 1.05 + (rnd() - 0.5) * 0.15;
    const len = (0.62 - f * 0.28) * (0.9 + rnd() * 0.2);
    const wid = 0.075 - f * 0.025;
    const dead = f < 0.2 && rnd() < 0.45;       // a few withered outer leaves
    dir.set(Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el));
    side.crossVectors(up, dir).normalize();
    const n = up.clone().addScaledVector(dir, -up.dot(dir)).normalize(); // leaf "top" normal
    const SEG = 3;
    const rows: number[][] = [];
    for (let s = 0; s <= SEG; s++) {
      const t = s / SEG;
      // droop: the tip falls below the straight line, more on outer leaves
      const droop = (1 - f) * 0.18 * t * t;
      const w = wid * (1 - t * 0.85) * (s === 0 ? 0.8 : 1);
      const c = v.set(0, 0.02, 0).addScaledVector(dir, len * t).addScaledVector(up, -droop * len).clone();
      tc.copy(dead ? DRY : LEAF).lerp(DARK, (1 - t) * 0.45);
      const centre = acc.v(c.x + n.x * w * 0.35, c.y + n.y * w * 0.35, c.z + n.z * w * 0.35, tc); // the keel (raised spine of the V)
      tc2.copy(dead ? DRY : LEAF).lerp(EDGE, 0.35);
      const l = acc.v(c.x - side.x * w, c.y - side.y * w, c.z - side.z * w, tc2);
      const r = acc.v(c.x + side.x * w, c.y + side.y * w, c.z + side.z * w, tc2);
      rows.push([l, centre, r]);
    }
    const tip = v.set(0, 0.02, 0).addScaledVector(dir, len * 1.12).addScaledVector(up, -(1 - f) * 0.2 * len);
    const ti = acc.v(tip.x, tip.y, tip.z, tc.set("#5a4a36"));
    for (let s = 0; s < SEG; s++) {
      const [a0, b0, c0] = rows[s], [a1, b1, c1] = rows[s + 1];
      acc.tri(a0, b0, b1); acc.tri(a0, b1, a1);
      acc.tri(b0, c0, c1); acc.tri(b0, c1, b1);
    }
    const [aL, bL, cL] = rows[SEG];
    acc.tri(aL, bL, ti); acc.tri(bL, cL, ti);
  }
  return acc.build();
}

/** BARREL CACTUS: a squat, deeply ribbed globe with a yellow crown of spines
 *  and flower buds. ~0.5 m at scale 1. */
export function barrelGeo(seed = 1): THREE.BufferGeometry {
  const acc = new Acc();
  const SKIN = new THREE.Color("#5f7048"), CROWN = new THREE.Color("#b49a4a"), BASE = new THREE.Color("#6e6448");
  const H = 0.5 + (seed % 3) * 0.03, R = 0.24;
  const ys = [-0.04, 0.08, 0.2, 0.32, 0.41, H - 0.03, H];
  const rad = [R * 0.92, R, R * 1.02, R * 0.94, R * 0.74, R * 0.36, 0];
  const path = ys.map((y) => new THREE.Vector3(0, y, 0));
  tube(acc, path, (i) => rad[i], 22, 0.17, (i, g, o) => {
    const y = ys[i];
    o.copy(SKIN).lerp(BASE, Math.max(0, 1 - y / 0.14) * 0.7).lerp(CROWN, Math.max(0, (y - 0.38) / (H - 0.38)) * 0.75);
    if (g) o.multiplyScalar(0.6);
    return o;
  });
  return acc.build();
}

/** SOAPTREE YUCCA: a forked trunk wearing a shaggy skirt of dead leaves, two
 *  spiky heads of thin blades, and a cream flower stalk on the taller head.
 *  ~2.5-3 m at scale 1. */
export function yuccaGeo(seed = 1): THREE.BufferGeometry {
  const rnd = mulberry(0x7cc + seed * 29);
  const acc = new Acc();
  const BARK = new THREE.Color("#5c4e3c"), SKIRT = new THREE.Color("#8d7650"), SKIRT_D = new THREE.Color("#5e4b33");
  const LEAF = new THREE.Color("#7f8c5c"), LEAF_T = new THREE.Color("#a5a774"), STALK = new THREE.Color("#8a7a52"), BLOOM = new THREE.Color("#e8dfbf");
  const heads: [number, number, number][] = [];
  // trunk: a short bole that forks into two leaning stems
  const forkY = 0.55 + rnd() * 0.25;
  tube(acc, [new THREE.Vector3(0, -0.1, 0), new THREE.Vector3(0, forkY * 0.5, 0), new THREE.Vector3(0.02, forkY, 0)],
    () => 0.085, 6, 0, (_i, _g, o) => o.copy(BARK));
  const a0 = rnd() * Math.PI * 2;
  for (let k = 0; k < 2; k++) {
    const az = a0 + k * Math.PI + (rnd() - 0.5) * 0.6;
    const len = k === 0 ? 1.1 + rnd() * 0.5 : 0.6 + rnd() * 0.35;
    const lx = Math.cos(az) * 0.32 * len, lz = Math.sin(az) * 0.32 * len;
    const top = new THREE.Vector3(lx, forkY + len, lz);
    tube(acc, [new THREE.Vector3(0.01, forkY - 0.05, 0), new THREE.Vector3(lx * 0.45, forkY + len * 0.5, lz * 0.45), top],
      (i) => 0.07 - i * 0.008, 6, 0, (_i, _g, o) => o.copy(BARK));
    heads.push([top.x, top.y, top.z]);
    // shaggy skirt: dead leaves hanging down the stem below the head (a
    // ragged cone, open at the bottom)
    const SK = 9, skH = 0.55;
    const ringTop: number[] = [], ringBot: number[] = [];
    for (let s = 0; s < SK; s++) {
      const a = (s / SK) * Math.PI * 2;
      const rb = 0.17 + rnd() * 0.07, hb = skH * (0.8 + rnd() * 0.35);
      ringTop.push(acc.v(top.x + Math.cos(a) * 0.1, top.y + 0.04, top.z + Math.sin(a) * 0.1, SKIRT));
      ringBot.push(acc.v(top.x + Math.cos(a) * rb, top.y - hb, top.z + Math.sin(a) * rb, tc.copy(SKIRT_D).multiplyScalar(0.85 + rnd() * 0.3)));
    }
    for (let s = 0; s < SK; s++) {
      const s1 = (s + 1) % SK;
      acc.tri(ringTop[s], ringBot[s1], ringTop[s1]); acc.tri(ringTop[s], ringBot[s], ringBot[s1]);
    }
    // the head: a burst of thin stiff blades (upper hemisphere + a ring of
    // near-horizontal ones), each a narrow lancet of two triangles
    const blades = 30;
    for (let b = 0; b < blades; b++) {
      const u = b / blades;
      const el = -0.15 + Math.acos(1 - u * 0.95) * 1.0;   // more blades low, fewer straight up
      const az2 = b * 2.39996;
      const L = 0.42 + rnd() * 0.2, w = 0.022;
      const dx = Math.cos(az2) * Math.cos(Math.PI / 2 - el), dy = Math.sin(Math.PI / 2 - el), dz = Math.sin(az2) * Math.cos(Math.PI / 2 - el);
      const sx = -Math.sin(az2) * w, sz = Math.cos(az2) * w;
      const bx = top.x, by = top.y + 0.02, bz = top.z;
      const i0 = acc.v(bx - sx, by, bz - sz, LEAF, 0);
      const i1 = acc.v(bx + sx, by, bz + sz, LEAF, 0);
      const i2 = acc.v(bx + dx * L * 0.5 + sx, by + dy * L * 0.5, bz + dz * L * 0.5 + sz, LEAF, 0.4);
      const i3 = acc.v(bx + dx * L, by + dy * L, bz + dz * L, LEAF_T, 1);
      acc.tri(i0, i1, i2); acc.tri(i0, i2, i3);
    }
  }
  // flower stalk on the tall head: a thin pole + a cluster of cream bells
  const [hx, hy, hz] = heads[0];
  const sTop = hy + 0.75 + rnd() * 0.3;
  tube(acc, [new THREE.Vector3(hx, hy, hz), new THREE.Vector3(hx + 0.02, (hy + sTop) / 2, hz), new THREE.Vector3(hx + 0.03, sTop, hz)],
    (i) => 0.022 - i * 0.006, 4, 0, (_i, _g, o) => o.copy(STALK), (i) => i * 0.5);
  for (let f = 0; f < 7; f++) {
    const y = sTop - 0.38 + f * 0.055, a = f * 2.4, rr = 0.06;
    const cx = hx + 0.03 + Math.cos(a) * rr, cz = hz + Math.sin(a) * rr, r = 0.04;
    const ids = [[r, 0, 0], [-r, 0, 0], [0, r, 0], [0, -r, 0], [0, 0, r], [0, 0, -r]]
      .map(([x, yy, z]) => acc.v(cx + x, y + yy, cz + z, BLOOM, 0.9));
    for (const [a1, b1, c1] of [[0, 2, 4], [4, 2, 1], [1, 2, 5], [5, 2, 0], [4, 3, 0], [1, 3, 4], [5, 3, 1], [0, 3, 5]]) acc.tri(ids[a1], ids[b1], ids[c1]);
  }
  return acc.build();
}

/** DEAD MESQUITE SNAG: a low twisted trunk that forks three times into bare,
 *  sun-bleached limbs. ~3 m at scale 1, spread ~1.5 m. */
export function snagGeo(seed = 1): THREE.BufferGeometry {
  const rnd = mulberry(0x5a9 + seed * 13);
  const acc = new Acc();
  const WOOD = new THREE.Color("#7a6f62"), DARK = new THREE.Color("#4a4037"), PALE = new THREE.Color("#a59c8e");
  const limb = (a: THREE.Vector3, dir: THREE.Vector3, len: number, r: number, depth: number): void => {
    const path: THREE.Vector3[] = [a.clone()];
    const d = dir.clone();
    const SEG = depth === 0 ? 4 : 3;
    for (let s = 1; s <= SEG; s++) {
      // gnarl: each segment kinks a little
      d.add(new THREE.Vector3((rnd() - 0.5) * 0.45, (rnd() - 0.4) * 0.2, (rnd() - 0.5) * 0.45)).normalize();
      path.push(path[s - 1].clone().addScaledVector(d, len / SEG));
    }
    const rEnd = depth >= 2 ? 0 : r * 0.55;
    tube(acc, path, (i) => r + (rEnd - r) * (i / SEG), depth === 0 ? 7 : 5, 0, (i, _g, o) => {
      // weathered grain: dark checks near the root, bleached toward the tips
      o.copy(WOOD).lerp(PALE, depth * 0.22 + (i / SEG) * 0.15).lerp(DARK, depth === 0 ? Math.max(0, 0.6 - i * 0.2) : 0);
      return o.multiplyScalar(0.85 + rnd() * 0.25);
    });
    if (depth >= 2) return;
    const end = path[SEG];
    const kids = depth === 0 ? 3 : 2;
    for (let k = 0; k < kids; k++) {
      const az = rnd() * Math.PI * 2;
      const nd = d.clone().multiplyScalar(0.6).add(new THREE.Vector3(Math.cos(az) * 0.75, 0.25 + rnd() * 0.35, Math.sin(az) * 0.75)).normalize();
      limb(end.clone().addScaledVector(d, -0.04), nd, len * (0.62 + rnd() * 0.18), rEnd * 0.85, depth + 1);
    }
  };
  limb(new THREE.Vector3(0, -0.15, 0), new THREE.Vector3(0.18, 1, -0.08).normalize(), 1.35, 0.16, 0);
  return acc.build();
}

/** Per species: one geometry per map (instances add yaw + scale variety). */
export const FLORA = {
  saguaro: saguaroGeo,
  organ: organPipeGeo,
  pear: pricklyPearGeo,
  agave: agaveGeo,
  barrel: barrelGeo,
  yucca: yuccaGeo,
  snag: snagGeo,
} as const;
export type FloraKind = keyof typeof FLORA;
/** Low ground cover (≤ knee-high, may grow anywhere on open floor) vs TALL
 *  plants (border band only, like the old tree line, and cast a shadow). */
export const LOW_FLORA: readonly FloraKind[] = ["pear", "agave", "barrel"];
export const TALL_FLORA: readonly FloraKind[] = ["saguaro", "organ", "yucca", "snag"];
